import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
  Card,
  Container,
  Typography,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';
import { UserListHead, UserListToolbar } from '../user';
import Scrollbar from '../../../components/scrollbar';

const StyledTableCell = styled(TableCell)`
  padding: 16px;
  text-align: center;
`;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? theme.palette.grey[100]
        : theme.palette.action.hover,
  },
}));

const TABLE_HEAD = [
  { id: 'alertname', label: <b>Alert Name</b>, alignRight: false },
  { id: 'jobname', label: <b>Job Name</b>, alignRight: false },
  { id: 'host', label: <b>Host</b>, alignRight: false },
  { id: 'description', label: <b>Description</b>, alignRight: false },
  { id: 'type', label: <b>Type</b>, alignRight: false },
  { id: 'status', label: <b>Status</b>, alignRight: false },
  { id: 'startsAt', label: <b>Start At</b>, alignRight: false },
  { id: 'endsAt', label: <b>End At</b>, alignRight: false },
  { id: 'receivedAt', label: <b>Received At</b>, alignRight: false },
];

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  const sortedArray = stabilizedThis.map((el) => el[0]);

  if (query) {
    return sortedArray.filter((item) =>
      item.alertname?.toLowerCase().includes(query.toLowerCase())
    );
  }

  return sortedArray;
}



export default function AlertList() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('alertname');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState([]);
  const token = localStorage.getItem('token');

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedDomainData, setSelectedDomainData] = useState(null);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.get(`${API_BASE_URL}/getAlertByUserId?userId=${userId}`, config);
      setData(response.data.alerts);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleConfirmDelete = async () => {
    setIsConfirmationDialogOpen(false);
    setIsLoading(true);
    try {
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      await axios.post(`${API_BASE_URL}/deleteDomain`, { _id: itemIdToDelete }, config);
      fetchData();
    } catch (error) {
      console.error('Error deleting domain:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmationDialogOpen(false);
    setItemIdToDelete(null);
  };

  const handleRequestSort = (event, property) => {
    if (property === 'serial') return;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };


  const getComparator = (order, orderBy) => (a, b) => {
    const getValue = (obj, key) => {
      switch (key) {
        case 'description':
          return obj.annotations?.description || '';
        case 'type':
          return obj.labels?.type || '';
        default:
          return obj[key] || '';
      }
    };

    let valA = getValue(a, orderBy);
    let valB = getValue(b, orderBy);

    // Normalize values for comparison
    valA = typeof valA === 'string' ? valA.toLowerCase() : String(valA);
    valB = typeof valB === 'string' ? valB.toLowerCase() : String(valB);

    if (order === 'asc') {
      return valA.localeCompare(valB);
    }
    return valB.localeCompare(valA);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const filteredUsers = applySortFilter(data, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredUsers.length && !!filterName;
  return (
    <Card>
      <Container maxWidth="xl">
        <Typography variant="h6" sx={{ marginTop: '15px', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Alert List
        </Typography>
        <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} searchName="Search Alert Name" />
        <Scrollbar>
          <TableContainer sx={{ minWidth: 1000, overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem' }}>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={data.length}
                      onRequestSort={handleRequestSort}
                    />
                    {data.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={TABLE_HEAD.length + 1}>
                            <Typography variant="body2" color="text.secondary" sx={{ justifyContent: 'center', display: 'flex' }}>
                              No Data Found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <TableBody>
                        {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                          const {
                            _id,
                            alertname,
                            annotations,
                            labels,
                            status,
                            receivedAt,
                            jobname,
                            host,
                            startsAt,
                            endsAt
                          } = row;

                          return (
                            <StyledTableRow hover key={_id} tabIndex={-1}>
                              <TableCell padding="normal">{page * rowsPerPage + index + 1}</TableCell>
                              <StyledTableCell>{alertname || '—'}</StyledTableCell>
                              <StyledTableCell>{jobname || '—'}</StyledTableCell>
                              <StyledTableCell>{host || '—'}</StyledTableCell>
                              <StyledTableCell>{annotations?.description || 'No description'}</StyledTableCell>
                              <StyledTableCell>{labels?.type || '—'}</StyledTableCell>
                              <StyledTableCell>{status || '—'}</StyledTableCell>
                              <StyledTableCell>{formatDate(startsAt)}</StyledTableCell>
                              <StyledTableCell>{status === 'firing' ? '—' : formatDate(endsAt)}</StyledTableCell>
                              <StyledTableCell>{formatDate(receivedAt)}</StyledTableCell>
                            </StyledTableRow>
                          );
                        })}

                        {emptyRows > 0 && (
                          <StyledTableRow style={{ height: 53 * emptyRows }}>
                            <StyledTableCell colSpan={6} />
                          </StyledTableRow>
                        )}
                      </TableBody>

                    )}

                    {isNotFound && (
                      <TableBody>
                        <TableRow key="is-not-found">
                          <TableCell align="center" colSpan={10} sx={{ py: 3 }}>
                            <Paper
                              sx={{
                                textAlign: 'center',
                              }}
                            >
                              {
                                !filterName &&
                                <Typography variant="body2" paragraph>
                                  No data found
                                </Typography>
                              }
                              {
                                filterName &&
                                <Typography variant="body2">
                                  No results found for &nbsp;
                                  <strong>&quot;{filterName}&quot;</strong>.
                                  <br /> Try checking for typos or using complete words.
                                </Typography>
                              }
                            </Paper>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </>
              )}
            </div>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Container>
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this?"
      />
      <Dialog open={isViewDialogOpen} onClose={() => setIsViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Last 5 Status Checks for {selectedDomainData?.domain}
          <IconButton
            aria-label="close"
            onClick={() => setIsViewDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedDomainData?.responses?.length > 0 ? (
            <List>
              {selectedDomainData.responses.map((resp, index) => (
                <ListItem
                  key={index}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee', mb: 1 }}
                >
                  <Typography variant="subtitle1" color={resp.status >= 400 ? "error" : "primary"}>
                    {resp.status >= 400 ? "❌" : "✅"} Status: {resp.status} ({resp.statusText})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    🕒 Checked At: {new Date(resp.checkedAt).toLocaleString()}
                  </Typography>

                  {resp.headers && (
                    <Accordion sx={{ width: '100%' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>🔍 View Headers</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {Object.entries(resp.headers).map(([key, value]) => (
                          <Typography key={key} variant="body2">
                            <strong>{key}:</strong> {value}
                          </Typography>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Accordion sx={{ width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>🧾 View Body</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>
                        {resp.body || "No response body"}
                      </pre>
                    </AccordionDetails>
                  </Accordion>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No status responses available.</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsViewDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  );
}
